// A disposable local materialization cache in IndexedDB. The server's log is
// always the source of truth; this only lets a returning session paint instantly
// and then fetch just the tail. Nuke it (or bump CACHE_VERSION) and a full
// replay rebuilds identical state.
//
// The cache now stores `seq` alongside the projection, and THAT is the change
// the substrate bought. Under General Text the fold cursor could not be
// persisted at all: a shard was a CRDT Y.Text, so a concurrent writer could
// insert content *before* a saved offset while the tab was closed, and resuming
// from it would silently skip events. A server-assigned seq has no such
// failure, because the server never reorders. So a warm boot asks for
// `?since=<seq>` and gets only what it missed.

import type { CrumEvent } from './events'
import { emptyState, type State } from './reducer'

const DB_NAME = 'crum'
const STORE = 'projection'

/**
 * The build that wrote the cache, stamped at bundle time (see vite.config.ts).
 *
 * `CACHE_VERSION` only catches a change to the projection's SHAPE, and that is not
 * the failure that actually happened: an installed copy running a pre-hosted bundle
 * painted its cached projection and then silently never updated, because the routes
 * it was calling had moved under `/api/w/<slug>/` and every fetch 404'd. Months of
 * the log were missing and nothing on screen said so. A cache written by a
 * different build is discarded, so a deploy can never be half-adopted.
 */
declare const __BUILD__: string
const BUILD = typeof __BUILD__ === 'string' ? __BUILD__ : 'dev'
// v12 REMOVED the `lineage` map: connections are derived from @mentions in a
// description now, and consent sits on the records the edge touches. v11 had added it.
//
// v10 added `note` on an item and `target` on a comment. A cached projection from
// v9 restores items with no `note`, and the row card calls `.trim()` on it — one
// undefined field is a blank app, which is exactly what this counter is for.
//
// v9 added the `groups` and `items` maps (boards). v8 was the standalone
// substrate: the projection paired with a server `seq` (see the top of this file).
//
// Bump this whenever the projection's SHAPE changes, so an older cached
// projection is discarded rather than half-read.
const CACHE_VERSION = 12

export interface CachedProjection {
  version: number
  /** the bundle that wrote it — see BUILD */
  build?: string
  workspaceId: string
  /** The last server seq folded into `state`. Resume point for the next boot. */
  seq: number
  state: SerializedState
}

interface SerializedState {
  entities: State['entities']
  entries: State['entries']
  comments: State['comments']
  commitments: State['commitments']
  groups: State['groups']
  items: State['items']
  support: State['support']
  dealStages: State['dealStages']
  /** the activity ring, not the whole log — see EVENT_RING in the reducer */
  events: CrumEvent[]
  applied: string[]
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function idbGet<T>(db: IDBDatabase, key: string): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(key)
    req.onsuccess = () => resolve(req.result as T | undefined)
    req.onerror = () => reject(req.error)
  })
}

function idbPut(db: IDBDatabase, key: string, value: unknown): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(value, key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

function cacheKey(workspaceId: string): string {
  return `proj:${workspaceId}`
}

/**
 * Restore a cached projection — or nothing, on the slightest doubt.
 *
 * The version number alone is NOT enough to trust a payload. A dev server serves
 * every save without typechecking, so a build can exist for a few seconds where
 * `CACHE_VERSION` is already bumped but a newly added map isn't being written yet;
 * anyone who loads the page in that window persists a payload that claims to be
 * current and isn't. The projection then carries an `undefined` where a record map
 * belongs, `Object.values()` throws on the next render, and the app won't start —
 * with no way for the user to tell why, because the log is fine.
 *
 * So every map is validated here, at the boundary. A full re-fold always produces
 * identical state, which makes "throw the cache away" the correct answer to any
 * surprise, and never a data-loss risk.
 */
export async function loadCache(
  workspaceId: string,
): Promise<{ state: State; seq: number } | null> {
  try {
    const db = await open()
    const cached = await idbGet<CachedProjection>(db, cacheKey(workspaceId))
    if (!cached || cached.version !== CACHE_VERSION || cached.workspaceId !== workspaceId)
      return null
    // A cache from another build is thrown away rather than trusted. A full refold
    // rebuilds identical state, so this costs one fetch and can never lose a write.
    if (cached.build !== BUILD) return null

    const c = cached.state as Partial<SerializedState> | undefined
    if (!c || !Array.isArray(c.events) || !Array.isArray(c.applied)) return null
    const maps = [
      c.entities,
      c.entries,
      c.comments,
      c.commitments,
      c.groups,
      c.items,
      c.support,
      c.dealStages,
    ]
    if (maps.some((m) => !m || typeof m !== 'object')) return null

    const s = emptyState()
    s.entities = c.entities!
    s.entries = c.entries!
    s.comments = c.comments!
    s.commitments = c.commitments!
    s.groups = c.groups!
    s.items = c.items!
    s.support = c.support!
    s.dealStages = c.dealStages!
    s.events = c.events
    s.applied = new Set(c.applied)
    // A missing or nonsense seq means "start from the beginning", which is always
    // correct — it costs a full refetch and dedupes to the same state.
    const seq = typeof cached.seq === 'number' && cached.seq >= 0 ? cached.seq : 0
    return { state: s, seq }
  } catch {
    return null // cache is best-effort; a miss just means a full fold
  }
}

export async function saveCache(workspaceId: string, state: State, seq: number): Promise<void> {
  try {
    const db = await open()
    const payload: CachedProjection = {
      version: CACHE_VERSION,
      build: BUILD,
      workspaceId,
      seq,
      state: {
        entities: state.entities,
        entries: state.entries,
        comments: state.comments,
        commitments: state.commitments,
        groups: state.groups,
        items: state.items,
        support: state.support,
        dealStages: state.dealStages,
        events: state.events,
        applied: [...state.applied],
      },
    }
    await idbPut(db, cacheKey(workspaceId), payload)
  } catch {
    // ignore — losing the cache only costs a re-parse next boot
  }
}
